/**
 * THE TYPE ROLES, and what each one IS — the leaf every other module reads.
 *
 * These tables have no dependencies, and that is the point. `typography` needs
 * them to resolve a size, and `legibility` needs the LEADING to measure a line
 * box; with them living in `typography`, which reads the density scale from
 * `legibility`, the two modules imported each other. That cycle happens to work
 * because every read is at call time, and it is exactly the shape that has
 * broken this build before — so the data lives here, where nothing can import
 * its way back (AG-2174).
 */

export const TYPE_ROLES = [
  'display', 'metric', 'headline', 'section', 'annotation', 'data', 'label', 'caption',
] as const;
export type TypeRole = (typeof TYPE_ROLES)[number];

/**
 * Each role's STEP on the modular scale, relative to body text at 0.
 *
 * These are ranks, not sizes. What they promise is the ORDER and the RELATIVE
 * distance — `display` is four steps above `label` on every board — and what a
 * step comes to in pixels is the design's `typeScale` below. That is the same
 * claim `HEIGHT_SCALE` and the emphasis tiers make, and it is the only claim a
 * renderer is entitled to make about somebody's typography.
 */
export const ROLE_STEP: Record<TypeRole, number> = {
  display: 4,
  metric: 5,
  headline: 2.5,
  section: 0,
  annotation: 0,
  data: -0.5,
  label: -1,
  caption: -1.5,
};

/**
 * How sharply the roles are distinguished — the design's own decision, as a
 * word (AG-2111).
 *
 * The RATIO between adjacent steps. A restrained board's display is a little
 * larger than its labels; a dramatic board's is several times larger. Both are
 * real designs, which is why this is stated rather than fixed: the previous
 * behaviour was one implicit ratio of roughly 1.0, which is why everything read
 * the same.
 */
export const TYPE_SCALE: Record<string, number> = {
  restrained: 1.125,   // a minor second — a dense report, an operations console
  balanced: 1.2,       // a minor third — the ordinary board
  dramatic: 1.333,     // a perfect fourth — an editorial or presentation board
};
export const DEFAULT_SCALE = 'balanced';

/** A role's weight. Hierarchy is carried by weight as much as by size, and a
 *  scale that only grows is a scale that shouts. */
export const ROLE_WEIGHT: Record<TypeRole, number> = {
  display: 700,
  metric: 800,
  headline: 650,
  section: 600,
  annotation: 500,
  data: 500,
  label: 500,
  caption: 400,
};

/** A role's LEADING — its line box as a multiple of its own size.
 *
 *  Leading had no owner. `legibility.labelLinePx` multiplied by a bare `1.4`
 *  with a comment calling it "the app's own line height", and every other
 *  surface that stacked two lines of type picked its own — `lineHeight: 1` on
 *  the fallback's figure, nothing at all on most. So the one property that
 *  decides whether two lines collide was set in several places and owned in
 *  none (AG-2174).
 *
 *  These are typographic facts rather than taste: a large figure set at its own
 *  height reads as one object and a paragraph set that tight is unreadable, so
 *  leading tightens as type grows. The numbers are RATIOS of the role's size,
 *  which is what leading is; nothing here is a pixel. */
export const ROLE_LEADING: Record<TypeRole, number> = {
  display: 1.1,
  metric: 1,
  headline: 1.2,
  section: 1.3,
  annotation: 1.45,
  data: 1.3,
  label: 1.4,
  caption: 1.45,
};

/** Whether a role is SET IN CAPS. A group label in caps reads as chrome rather
 *  than as content, which is why `section` is the one that is — and it was
 *  decided by whoever wrote each piece of markup until now. */
export const ROLE_CASE: Partial<Record<TypeRole, string>> = { section: 'uppercase' };

/** Whether a role's digits are TABULAR — same advance width, so figures in a
 *  column line up. True wherever numbers are read against each other. */
export const ROLE_FIGURES: Partial<Record<TypeRole, string>> = {
  metric: 'tabular-nums',
  data: 'tabular-nums',
};

/** A role's leading, as a multiple of its own size. Exported so a caller
 *  computing a line BOX in pixels uses the same answer the CSS does. */
export function roleLeading(role: TypeRole): number {
  return ROLE_LEADING[role];
}

/** A role's tracking, in em. Only the two that need it: a small uppercase group
 *  label needs air between letters, and a large figure needs less. */
export const ROLE_TRACKING: Partial<Record<TypeRole, number>> = {
  section: 0.08,
  display: -0.015,
  metric: -0.02,
};


/** The ratio a named scale means, or the default one. */
export function scaleRatio(word?: string): number {
  const asked = String(word || '').trim().toLowerCase();
  return TYPE_SCALE[asked] ?? TYPE_SCALE[DEFAULT_SCALE];
}

/** A role's size, from a base and a ratio — the geometric step, and nothing else.
 *
 *  ⭐ LIVES HERE SO THERE IS ONE OF IT (AG-2205). `typography` needs it to publish
 *  the type variables, and `legibility` needs it to answer how much room a tile
 *  gets before anything is drawn. Both used to be impossible at once: `typography`
 *  imports `legibility`, so `legibility` could not import back without a cycle,
 *  and the alternative was two copies of the same formula drifting apart. This
 *  module imports nothing, so both can call it.
 */
export function roleSizeFrom(role: TypeRole, basePx: number, ratio: number): number {
  return basePx * Math.pow(ratio, ROLE_STEP[role]);
}


/**
 * THE SURFACES A BOARD SETS TYPE ON, and which role each uses by default.
 *
 * ⭐ THE LAST PIECE OF TYPOGRAPHY THE RENDERER OWNED (AG-2254). AG-2249 made
 * every PROPERTY of a role designable — a board can set its metrics lighter, its
 * section labels in sentence case, its headline tighter. What it could not do was
 * decide WHICH ROLE a surface uses. The stylesheet said `.dt-title` reads
 * `--dt-type-headline` and `.dt-tab` reads `--dt-type-label`, and no design could
 * reassign either, so a board that wanted its tile titles to LEAD — set in
 * `display`, the way an editorial page leads — had no way to ask. Every board in
 * the product used the same five assignments.
 *
 * ⚠️ THIS TABLE IS NOT THE SAME AUTHORITY UNDER A NEW NAME. It is the answer for
 * a surface the design did not speak about, which is a floor a board inherits
 * rather than a taste it is stuck with. The stylesheet no longer names a role at
 * all for these surfaces: it reads `--dt-role-<surface>-*`, and `typographyVars`
 * points that at whichever role the design assigned.
 */
export const TYPE_SURFACES = ['title', 'subtitle', 'tab', 'section', 'empty'] as const;
export type TypeSurface = (typeof TYPE_SURFACES)[number];

export const SURFACE_ROLE: Record<TypeSurface, TypeRole> = {
  /** A tile's own title. */
  title: 'headline',
  /** The question or note under a tile's title. */
  subtitle: 'caption',
  /** One tab in a navigation bar. */
  tab: 'label',
  /** A section label over a group of tiles. */
  section: 'section',
  /** The message where a board, or a view, has nothing in it. */
  empty: 'label',
};
